import { JSXElement } from '@/common/types';
import { Loading } from '@/components/loading';
import { AuthState, RouteType } from './types';
import { useCloudAuth, useRedirect, useUnauthenticatedCloudUser } from '@itential/cloud-auth-client';
import { PropsWithChildren, useEffect } from 'react';
import { env } from '@/utils/shared/env';
import { useLocation, useNavigate } from 'react-router';
import { Paths } from '@/router/constants';
import { useHasRoles } from '@/hooks/utils';
import { RolesEnum } from '@/enums/roles.enums';
import { useHasJobViewerEntitlement } from '@/hooks/utils/use-has-entitlement';
import { performSignOut } from '@/utils/auth/perform-sign-out';

interface RouteGuardProps extends PropsWithChildren {
    routeType: RouteType;
}

export function RouteGuard({ children, routeType }: RouteGuardProps): JSXElement {
    const { state } = useCloudAuth();
    const canViewMetadata = useHasRoles([RolesEnum.METADATA]);
    const hasEntitlement = useHasJobViewerEntitlement();
    switch (state.state) {
        case AuthState.SIGNED_IN:
            if (routeType === RouteType.AUTH) return <RedirectToApp />;
            if (routeType === RouteType.RESTRICTED) return <>{children}</>;
            if (!hasEntitlement) return <RedirectToNoEntitlement />;
            if (!canViewMetadata) return <RedirectToNoPermissions />;
            return <>{children}</>;
        case AuthState.SIGNED_OUT:
            if (routeType === RouteType.PRIVATE || routeType === RouteType.RESTRICTED)
                return <RedirectToSignIn />;
            return <>{children}</>;
        case AuthState.ERROR:
            return <RedirectToSignIn />;
        case AuthState.LOADING:
        default:
            return <Loading />;
    }
}

export function RedirectToSignIn(): null {
    const location = useLocation();
    const [, setRedirect] = useRedirect();

    useEffect(() => {
        setRedirect(location.pathname + location.search);
        performSignOut();
    }, [location.pathname, setRedirect, location.search]);

    return null;
}

export function RedirectToNoEntitlement(): null {
    const [redirect] = useRedirect();
    const navigate = useNavigate();
    useEffect(() => {
        const to = Paths.RESTRICTED;
        navigate(to, { replace: true });
    }, [redirect, navigate]);

    return null;
}

export function RedirectToNoPermissions(): null {
    const [redirect] = useRedirect();
    const navigate = useNavigate();

    useEffect(() => {
        const to = Paths.NO_PERMISSIONS;
        navigate(to, { replace: true });
    }, [redirect, navigate]);

    return null;
}

export function RedirectToApp(): null {
    const [redirect] = useRedirect();

    useEffect(() => {
        const to = redirect ?? Paths.BASE;
        window.location.href = to;
    }, [redirect]);

    return null;
}

/** Login page for development environment */
export function SignIntoDevRealm(): null {
    const { setRealm } = useCloudAuth();
    const { signIn, isSigningIn } = useUnauthenticatedCloudUser();

    useEffect(() => {
        if (!isSigningIn) {
            setRealm(env.VITE_REALM);
            signIn();
        }
    }, [setRealm, signIn, isSigningIn]);

    return null;
}
